import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { doc, runTransaction, deleteField, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { approveWorkspaceFiles } from '../../../services/workspaceApprovals';
import { uploadApprovalBlockedReason } from '../../../utils/workspaceApproval';
import { formatText } from '../../common/TextFormatterProvider';
import { formatDisplayValue } from '../../../utils/tagValueUtils';

// Chip state for one triage card: the list of destination table ids. Starts on
// the decision the document already holds — the person's approved allocation
// (kept on the document when the relay bounces it back) — and only then on the
// model's proposal; user edits replace it wholesale.
export function initialTriageSelection(file) {
  return file?.allocations?.table_ids || file?.triage?.table_ids || [];
}

// The card's action, derived from the CURRENT chips rather than the stored
// proposal, so edits re-derive it live. Null while no table is selected.
export function deriveTriageAction(file, selection) {
  const tableIds = selection || [];
  return tableIds.length > 0 ? { table_ids: tableIds } : null;
}

// The destination tables of a derived action, in chip order.
export function triageActionTableIds(action) {
  return action?.table_ids || [];
}

// One workspace staging file awaiting its destination-table decision.
function TriageFileCard({
  file, upload, companyId, tables, selection, onSelectionChange, onApproved, onError, batchLabel,
}) {
  const [isApproving, setIsApproving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const triage = file.triage || {};
  const selectedTableIds = selection || [];
  const action = deriveTriageAction(file, selectedTableIds);
  const approvalBlockedReason = uploadApprovalBlockedReason(upload);
  const registrationPending = (Boolean(file.upload_id) && !upload) || upload?.status === 'registering';
  // Attributes the whole drop carries. `tables` arrives pre-filtered to the tables
  // that can store them, so an empty list here means no destination qualifies.
  const packAttributes = Object.entries(file.pack_attributes || {});
  const noCarrierTables = packAttributes.length > 0 && (tables || []).length === 0;
  // A table already holding a chip is not offered again — the file is copied whole to
  // each destination exactly once.
  const addableTables = (tables || []).filter(t => !selectedTableIds.includes(t.id));

  const tableLabel = (tableId) => {
    const table = (tables || []).find(t => t.id === tableId);
    return formatText(table?.name || tableId);
  };

  // The destination table is named by the bucket this card sits in, so the button does
  // not repeat it; a fan-out states its size rather than listing names.
  const actionLabel = () => {
    const count = selectedTableIds.length;
    return count > 1 ? `Approve · copy to ${count} tables` : 'Approve';
  };

  const handleRemoveTable = (tableId) => {
    onSelectionChange(file.id, selectedTableIds.filter(id => id !== tableId));
  };

  const handleAddTable = (tableId) => {
    if (!tableId || selectedTableIds.includes(tableId)) return;
    onSelectionChange(file.id, [...selectedTableIds, tableId]);
  };

  const handleApprove = async () => {
    if (!action || approvalBlockedReason) return;
    setIsApproving(true);
    try {
      const result = await approveWorkspaceFiles(companyId, file.upload_id, [{ file, action }]);
      if (result.approved.length > 0) onApproved(file.id, action);
      else onError(result.skipped[0].reason);
    } catch (err) {
      console.error('Error approving triage allocation:', err);
      onError('Failed to approve allocation');
    } finally {
      setIsApproving(false);
    }
  };

  const handleDelete = async () => {
    if (registrationPending) return;
    setIsDeleting(true);
    try {
      const stagingRef = doc(db, `users/${companyId}/unallocated_files`, file.id);
      await runTransaction(db, async (tx) => {
        const stagingSnap = await tx.get(stagingRef);
        if (!stagingSnap.exists()) return;
        const uploadId = stagingSnap.data().upload_id;
        if (!uploadId) {
          tx.delete(stagingRef);
          return;
        }
        const uploadRef = doc(db, `users/${companyId}/uploads/${uploadId}`);
        const uploadSnap = await tx.get(uploadRef);
        if (!uploadSnap.exists()) {
          tx.delete(stagingRef);
          return;
        }
        const uploadData = uploadSnap.data();
        if (uploadData.status === 'registering') {
          throw new Error('This batch is still uploading. Wait until it finishes.');
        }
        const manifest = { ...(uploadData.raw_manifest || {}) };
        delete manifest[file.id];
        // Drained: the batch is registered and this cancel removed its last pending
        // member, so this transaction also seals the routing children and completes
        // the upload (nothing else will).
        const drained = uploadData.status === 'uploaded'
          && !Object.values(manifest).some(state => state === 'pending');
        const anyMemberError = Object.values(manifest).some(state => state === 'error');

        // All reads must precede the writes below — the JS SDK rejects a read
        // issued after a queued write.
        const childRefs = [];
        const childSnaps = [];
        if (drained) {
          const routedTableIds = Array.from(new Set(
            Object.values(uploadData.routing || {}).flatMap(entry => entry?.table_ids || [])
          ));
          for (const tableId of routedTableIds) {
            const childRef = doc(db, `users/${companyId}/tables/${tableId}/uploads/${uploadId}`);
            childRefs.push(childRef);
            childSnaps.push(await tx.get(childRef));
          }
        }

        const uploadUpdates = {
          [`raw_manifest.${file.id}`]: deleteField(),
          [`routing.${file.id}`]: deleteField(),
        };
        if (drained) {
          uploadUpdates.status = anyMemberError ? 'completed_with_errors' : 'completed';
          uploadUpdates.completed_at = serverTimestamp();
          childSnaps.forEach((childSnap, index) => {
            // Any other status means the child is already past sealing — skip it.
            if (childSnap.exists() && childSnap.data().status === 'registering') {
              tx.update(childRefs[index], { status: 'uploaded', sealed_at: serverTimestamp() });
            }
          });
        }
        tx.update(uploadRef, uploadUpdates);
        tx.delete(stagingRef);
      });
    } catch (error) {
      console.error('Error deleting workspace file:', error);
      onError(error.message || 'Failed to delete file.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flexGrow: 1, mr: 1, minWidth: 0 }}>
            <Typography variant="subtitle2">{file.fileName}</Typography>
            {batchLabel && <Chip label={batchLabel} size="small" variant="outlined" />}
          </Box>
          <IconButton
            size="small"
            onClick={handleDelete}
            disabled={isDeleting || isApproving || registrationPending}
            sx={{ p: 0 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        {packAttributes.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {packAttributes.map(([category, value]) => (
              <Chip
                key={category}
                label={`${formatText(category)}: ${formatDisplayValue(value)}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        )}

        {file.status === 'analysing' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2">Analysing...</Typography>
            <CircularProgress size={16} sx={{ ml: 1 }} />
          </Box>
        ) : file.status === 'Failed' ? (
          <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
            {file.error || 'Triage failed. Delete the file and upload it again.'}
          </Alert>
        ) : (
          <>
            {/* A pending card carrying an error bounced back from the relay. */}
            {file.error && (
              <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                {file.error}
              </Alert>
            )}

            {noCarrierTables ? (
              <Typography variant="body2" color="warning.main">
                {`No tables can store ${packAttributes.map(([category]) => formatText(category)).join(', ')} `
                  + '— add the attribute column to a table, or delete this file and re-drop it '
                  + 'without pack attributes.'}
              </Typography>
            ) : (
              <>
                {/* The model's one-sentence justification. A low-confidence call is said
                    so explicitly, because the chips look identical either way. */}
                {triage.reason && (
                  <Typography
                    variant="caption"
                    color={triage.confidence === 'low' ? 'warning.main' : 'text.secondary'}
                    sx={{ display: 'block' }}
                  >
                    {triage.confidence === 'low' ? `Low confidence — ${triage.reason}` : triage.reason}
                  </Typography>
                )}

                {selectedTableIds.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    No match — add the table(s) this file belongs to.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {selectedTableIds.map((tableId) => (
                      <Chip
                        key={tableId}
                        label={tableLabel(tableId)}
                        size="small"
                        onDelete={registrationPending || isApproving ? undefined : () => handleRemoveTable(tableId)}
                      />
                    ))}
                  </Box>
                )}

                <FormControl disabled={registrationPending || isApproving} fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel id={`triage-add-table-${file.id}`}>Add table</InputLabel>
                  <Select
                    labelId={`triage-add-table-${file.id}`}
                    // Never holds a value: a choice is committed to the chips and the
                    // control returns to its label, ready for the next one.
                    value=""
                    onChange={(e) => handleAddTable(e.target.value)}
                    label="Add table"
                  >
                    {addableTables.map((table) => (
                      <MenuItem key={table.id} value={table.id}>
                        {formatText(table.name || table.id)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="small"
                sx={{ width: '100%' }}
                onClick={handleApprove}
                disabled={!action || isApproving || Boolean(approvalBlockedReason)}
              >
                {actionLabel()}
              </Button>
              {approvalBlockedReason && <Typography variant="caption" color="text.secondary">{approvalBlockedReason}</Typography>}
              {selectedTableIds.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Each table receives the full file — parts that don't belong there can be ignored
                  after cleaning, and Decode remembers the ignore for next time.
                </Typography>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default TriageFileCard;
